import React, { Children, Component } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { products } from '../data/products';
import { ProductCard } from '../components/ProductCard';
import { EnhancedScrollReveal } from '../components/common/EnhancedScrollReveal';
// Core Components
import { HeroSection } from '../components/home/HeroSection';
import { TrustIndicators } from '../components/home/TrustIndicators';
import { ValueProposition } from '../components/home/ValueProposition';
import { HowItWorks } from '../components/home/HowItWorks';
import { ProductCategories } from '../components/home/ProductCategories';
import { IngredientsShowcase } from '../components/home/IngredientsShowcase';
import { QualityShowcase } from '../components/home/QualityShowcase';
import { ManufacturingProcess } from '../components/home/ManufacturingProcess';
import { ExpertTeam } from '../components/home/ExpertTeam';
import { ResourcesHub } from '../components/home/ResourcesHub';
import { TestimonialsSection } from '../components/TestimonialsSection';
import { ToysShowcase } from '../components/home/ToysShowcase';
// New Phase 4 Components
import { InteractiveNutritionCalculator } from '../components/home/InteractiveNutritionCalculator';
import { ScientificEvidence } from '../components/home/ScientificEvidence';
import { CustomerSuccessDashboard } from '../components/home/CustomerSuccessDashboard';
import { SustainabilityImpact } from '../components/home/SustainabilityImpact';
import { GuaranteeSection } from '../components/home/GuaranteeSection';
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1
    }
  }
};
// Reusable Section Header Component for Consistency
const SectionHeader = ({
  badge,
  title,
  subtitle,
  centered = true,
  className = ''
}: {
  badge?: string;
  title: React.ReactNode;
  subtitle?: string;
  centered?: boolean;
  className?: string;
}) => <div className={`mb-16 ${centered ? 'text-center' : 'text-left'} ${className}`} data-id="element-6183">
    {badge && <EnhancedScrollReveal direction="up" delay={0.1} data-id="element-6184">
        <span className="inline-block px-4 py-1.5 rounded-full bg-primary-50 border border-primary-100 text-primary-700 text-xs font-bold uppercase tracking-wider mb-4 shadow-soft-sm" data-id="element-6185">
          {badge}
        </span>
      </EnhancedScrollReveal>}

    <EnhancedScrollReveal direction="up" delay={0.2} data-id="element-6186">
      <h2 className="text-4xl md:text-5xl font-serif font-bold text-charcoal-900 mb-4 leading-tight" data-id="element-6187">
        {title}
      </h2>
    </EnhancedScrollReveal>

    {subtitle && <EnhancedScrollReveal direction="up" delay={0.3} data-id="element-6188">
        <p className="text-lg text-charcoal-600 max-w-2xl mx-auto font-light leading-relaxed" data-id="element-6189">
          {subtitle}
        </p>
      </EnhancedScrollReveal>}
  </div>;
export function HomePage() {
  // Get featured food products (3 products)
  const featuredFoodProducts = products.filter(p => p.featured && (p.category === 'dog-food' || p.category === 'cat-food')).slice(0, 3);
  // Get featured toy products (3 products)
  const featuredToyProducts = products.filter(p => p.category === 'toys').slice(0, 3);
  // Combine for mixed showcase (6 products total - balanced)
  const featuredMixedProducts = [...featuredFoodProducts, ...featuredToyProducts];
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.6,
    ease: [0.16, 1, 0.3, 1]
  }} className="flex flex-col min-h-screen bg-white font-sans overflow-x-hidden" data-id="element-6190">
      {/* 1. Hero Section: Hook Emocional Dual */}
      <HeroSection data-id="element-6191" />

      {/* 2. Trust Indicators: Credibilidad */}
      <TrustIndicators data-id="element-6192" />

      {/* 3. Product Categories: The Two Pillars (Navigation) */}
      <section className="py-20 lg:py-28 bg-white relative" data-id="element-6193">
        <div className="container-custom" data-id="element-6194">
          <SectionHeader badge="Nuestro Catálogo" title={<>
                Todo lo que tu mascota necesita para{' '}
                <span className="text-primary-600" data-id="element-6196">prosperar</span>
              </>} subtitle="Nutrición científica y juego enriquecedor. Dos pilares fundamentales para una vida larga y feliz." data-id="element-6195" />
          <ProductCategories data-id="element-6197" />
        </div>
      </section>

      {/* 4. Value Proposition: Why Both Matter */}
      <ValueProposition data-id="element-6198" />

      {/* NEW: Interactive Nutrition Calculator */}
      <InteractiveNutritionCalculator data-id="element-6199" />

      {/* 5. Toys Showcase: Deep Dive into Play */}
      <ToysShowcase data-id="element-6200" />

      {/* 6. Featured Products: Mixed Showcase */}
      <section className="py-20 lg:py-28 bg-gradient-to-br from-gray-50 to-white relative overflow-hidden" data-id="element-6201">
        {/* Decorative Background Elements */}
        <div className="absolute top-1/4 left-0 w-96 h-96 bg-primary-100/30 rounded-full blur-3xl opacity-50 pointer-events-none" data-id="element-6202" />
        <div className="absolute bottom-1/4 right-0 w-96 h-96 bg-secondary-100/30 rounded-full blur-3xl opacity-50 pointer-events-none" data-id="element-6203" />

        <div className="container-custom max-w-7xl mx-auto relative z-10" data-id="element-6204">
          <SectionHeader badge="Lo Más Popular" title="Favoritos de nuestra comunidad" subtitle="Descubre los productos mejor valorados por dueños y expertos: nutrición que funciona y juguetes que enamoran." data-id="element-6205" />

          <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8" data-id="element-6206">
            {featuredMixedProducts.map((product, index) => <ProductCard key={product.id} product={product} featured index={index} data-id="element-6207" />)}
          </motion.div>

          <EnhancedScrollReveal direction="up" delay={0.4} className="text-center mt-16 w-full" data-id="element-6208">
            <Link to="/productos" data-id="element-6209">
              <Button variant="outline" size="lg" className="rounded-full px-10 py-5 text-base shadow-soft-sm hover:shadow-soft-md transition-all bg-white border-primary-200 text-primary-800 hover:bg-primary-50 hover:border-primary-300 font-bold" data-id="element-6210">
                Ver Catálogo Completo
              </Button>
            </Link>
          </EnhancedScrollReveal>
        </div>
      </section>

      {/* 7. How It Works: Process */}
      <HowItWorks data-id="element-6211" />

      {/* 8. Quality Showcase: Definitive Quality Section */}
      <QualityShowcase data-id="element-6212" />

      {/* 9. Manufacturing Process: Definitive Process Section */}
      <section className="py-20 lg:py-28 bg-white" data-id="element-6213">
        <div className="container-custom" data-id="element-6214">
          <SectionHeader badge="Ciencia y Artesanía" title="Tecnología al servicio de la nutrición" subtitle="Combinamos métodos tradicionales de cocción lenta con la última tecnología en seguridad alimentaria." data-id="element-6215" />
          <ManufacturingProcess data-id="element-6216" />
        </div>
      </section>

      {/* 10. Ingredients Showcase: Transparencia */}
      <section className="py-20 lg:py-28 bg-gradient-to-br from-emerald-50/50 to-white" data-id="element-6217">
        <div className="container-custom" data-id="element-6218">
          <SectionHeader badge="Ingredientes Reales" title={<>
                Nada que esconder,{' '}
                <span className="text-emerald-600" data-id="element-6220">todo que mostrar</span>
              </>} subtitle="Cada ingrediente tiene un propósito. Sin rellenos, sin subproductos, sin letra pequeña." data-id="element-6219" />
          <IngredientsShowcase data-id="element-6221" />
        </div>
      </section>

      {/* NEW: Scientific Evidence */}
      <ScientificEvidence data-id="element-6222" />

      {/* 11. Testimonials: Social Proof */}
      <TestimonialsSection data-id="element-6223" />

      {/* NEW: Sustainability Impact */}
      <SustainabilityImpact data-id="element-6224" />

      {/* NEW: Customer Success Dashboard */}
      <CustomerSuccessDashboard data-id="element-6225" />

      {/* 12. Expert Team: Autoridad */}
      <ExpertTeam data-id="element-6226" />

      {/* 13. Resources: Educación */}
      <ResourcesHub data-id="element-6227" />

      {/* NEW: Guarantee Section */}
      <GuaranteeSection data-id="element-6228" />
    </motion.div>;
}